import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import { PackWithBuyButton } from '../PackWithBuyButton';
import { PremiumPackTitle } from './PremiumPackTitle';
import type {
  PremiumPack_cardPack,
  PremiumPack_cardPackGroup,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  padding: var(--double-unit);
  width: 100%;
  background:
    linear-gradient(to bottom, transparent 0%, var(--c-black) 100%),
    var(--background-image) bottom -10% center/cover no-repeat;
  border-radius: var(--double-unit);
  --border-gradient: linear-gradient(
    to bottom,
    var(--league-color) 0%,
    transparent 20%
  );
  ${borderGradientStyle};
  overflow: hidden;
  justify-content: space-between;
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  top: -1px;
  height: var(--double-unit);
  width: 200%;
  transform: rotate(180deg);
`;

type Props = {
  cardPack: PremiumPack_cardPack;
  cardPackGroup: PremiumPack_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, slug: string) => void;
  children?: ReactNode;
};
export const PremiumPack = ({
  cardPack,
  cardPackGroup,
  onClickBuy,
  children,
}: Props) => {
  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroup.id),
  });

  return (
    <Root
      style={
        {
          '--background-image': `url(${bgImage})`,
          '--league-color': cardPack.primaryColor,
        } as CSSProperties
      }
    >
      <StyledNeonEdge
        color={cardPack.primaryColor}
        showHalo="always"
        showNeon="always"
      />
      <PremiumPackTitle cardPackGroup={cardPackGroup} cardPack={cardPack} />
      <PackWithBuyButton
        cardPack={cardPack}
        cardPackGroupId={cardPackGroup.id}
        onClickBuy={onClickBuy}
        gap={3}
        isPremiumPack
      />
      {children}
    </Root>
  );
};

PremiumPack.fragments = {
  cardPack: gql`
    fragment PremiumPack_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      ...PremiumPackTitle_cardPack
      ...PackWithBuyButton_cardPack
    }
    ${PackWithBuyButton.fragments.cardPack}
    ${PremiumPackTitle.fragments.cardPack}
  ` as TypedDocumentNode<PremiumPack_cardPack>,
  cardPackGroup: gql`
    fragment PremiumPack_cardPackGroup on CardPackGroup {
      id
      ...PremiumPackTitle_cardPackGroup
    }
    ${PremiumPackTitle.fragments.cardPackGroup}
  ` as TypedDocumentNode<PremiumPack_cardPackGroup>,
};
